import { ApolloError } from "@apollo/client";
import { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useBrandConfig } from "src/brand/config";
import { Brand } from "src/core/constants/brand";
import { Status } from "src/core/constants/folders";
import { useFolderListContext } from "src/core/contexts/FolderListContext";
import { useUser } from "src/core/contexts/UserContext";
import { useFolderListDataSource } from "src/core/hooks/useFolderListDataSource";
import { usePermissions } from "src/core/hooks/usePermissions";
import { DataSourceKeyOf } from "src/core/types/folderDataSource";
import { FolderListOf } from "src/core/types/folderListStrategy";
import { buildQueryVariablesFolderFilesPage } from "src/core/utils/folders";

let latestRequestId = 0;

export const useFolderList = ({
  sorting,
  pagination,
}: {
  sorting: SortingState;
  pagination: PaginationState;
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const brandConfig = useBrandConfig();
  const { registry } = useFolderListDataSource();

  const strategy = brandConfig.strategies.folder.list;
  const dataSource = brandConfig.strategies.folder.data;

  const [data, setData] = useState<FolderListOf<Brand>[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const statusCard = location.state?.status;
  const [currentTab, setCurrentTab] = useState<string>(() => {
    return (statusCard as string) ?? Status.DRAFT;
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [_error, setError] = useState<ApolloError | undefined>(undefined);

  const columns = useMemo<ColumnDef<FolderListOf<Brand>>[]>(() => {
    return strategy.buildColumns({ status: currentTab as Status, t });
  }, [strategy, currentTab, t]);

  const sourceByStatus = dataSource?.sourceByStatus;
  const mapRawToRows = dataSource?.mapRawToRows;
  const { ownerUserIdFilter, followedByUserIdFilter, myFoldersFilter } = useFolderListContext();
  const { user } = useUser();
  const { hasRole } = usePermissions();

  const handleTabChange = useCallback(
    async (value: string, sortingState: SortingState, paginationState: PaginationState) => {
      const requestId = ++latestRequestId;

      setLoading(true);
      setError(undefined);

      const offset = paginationState.pageIndex * paginationState.pageSize;
      const variables = buildQueryVariablesFolderFilesPage(
        value,
        sortingState,
        paginationState.pageSize,
        offset,
        ownerUserIdFilter,
        followedByUserIdFilter,
        myFoldersFilter,
        user,
        hasRole
      );

      try {
        if (!sourceByStatus || !mapRawToRows) {
          throw new Error("Aucune source de données configurée pour cette marque.");
        }

        // Récupération de la clé de la source de données
        const sourceKey: DataSourceKeyOf<Brand> = sourceByStatus(value as Status);
        // Récupération des données brutes
        const result = await registry[sourceKey](variables);
        // Mapping des données brutes vers le format de la liste
        const rows = mapRawToRows(value as Status, result.data);

        if (requestId !== latestRequestId) {
          return;
        }

        setData(rows as FolderListOf<Brand>[]);
        setTotalCount(result.totalCount);
      } catch (error) {
        console.error("useFolderList error", error);
        setError(error as ApolloError);
      } finally {
        setLoading(false);
      }
    },
    [
      registry,
      sourceByStatus,
      mapRawToRows,
      ownerUserIdFilter,
      followedByUserIdFilter,
      myFoldersFilter,
      user,
      hasRole,
    ]
  );

  const stableSorting = useMemo(() => sorting, [sorting]);
  const stablePagination = useMemo(() => pagination, [pagination]);
  const previousTabRef = useRef<string>(currentTab);

  // Pagination par défaut pour réinitialiser complètement lors d'un changement d'onglet
  const defaultPagination: PaginationState = useMemo(
    () => ({
      pageIndex: 0,
      pageSize: 10,
    }),
    []
  );

  useEffect(() => {
    if (currentTab) {
      // Si l'onglet a changé, réinitialiser toute la pagination
      const isTabChange = previousTabRef.current !== currentTab;
      const paginationToUse: PaginationState = isTabChange ? defaultPagination : stablePagination;

      // Handle sorting and pagination changes for the current tab
      handleTabChange(currentTab, stableSorting, paginationToUse);

      // Mettre à jour la référence de l'onglet précédent
      previousTabRef.current = currentTab;
    }
  }, [stableSorting, stablePagination, currentTab, handleTabChange, defaultPagination]);

  return {
    currentTab,
    setCurrentTab,
    columns,
    data,
    loading,
    totalCount,
    setLoading,
  };
};
